import { useState } from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { ConfirmationModal } from "@platform/design-system";

import { editTotalsBlock } from "../totalsBlockOperations";
import { buildToggleCreditsHiddenDiff } from "../buildPatchDiff";
import { resolveTotalsBlockName } from "../../totalsBlockName";

import { TotalsBlockDialog } from "./TotalsBlockDialog";

import type { FinancialsGridContext } from "../types";

interface TotalsBlockMenuProps {
  blockId: string;
  context: FinancialsGridContext;
}

export const TotalsBlockMenu = ({
  blockId,
  context,
}: TotalsBlockMenuProps): React.JSX.Element => {
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);

  const isDefault = blockId === "default";
  const block = context.blockById.get(blockId);
  const blockName = resolveTotalsBlockName(block);
  const creditsHidden = block?.creditsRow.hidden ?? false;
  const menuOpen = Boolean(anchorElement);

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>): void => {
    event.stopPropagation();
    setAnchorElement(event.currentTarget);
  };

  const handleCloseMenu = (): void => {
    setAnchorElement(null);
  };

  const handleOpenEditDialog = (): void => {
    handleCloseMenu();
    setEditDialogOpen(true);
  };

  const handleOpenAddDialog = (): void => {
    handleCloseMenu();
    setAddDialogOpen(true);
  };

  const handleOpenConfirmRemove = (): void => {
    handleCloseMenu();
    setConfirmRemoveOpen(true);
  };

  const handleAddBlock = (name: string, coverageIds: string[]): void => {
    const position = context.payload.totalsBlocks.filter(
      (b) => b.kind === "custom",
    ).length;
    context.createTotalsBlock(name, coverageIds, position);
  };

  const handleEditBlock = (name: string, coverageIds: string[]): void => {
    const updatedBlocks = editTotalsBlock(
      context.payload.totalsBlocks,
      blockId,
      name,
      coverageIds,
    );
    context.queueChange({ totalsBlocks: updatedBlocks });
  };

  const handleShowCredits = (): void => {
    context.queueChange(
      buildToggleCreditsHiddenDiff(blockId, true, context.payload.totalsBlocks),
    );
    handleCloseMenu();
  };

  const handleRemoveBlock = (): void => {
    context.deleteTotalsBlock(blockId);
    setConfirmRemoveOpen(false);
  };

  return (
    <div data-testid={`totals-block-menu-${blockId}`}>
      <IconButton size="small" onClick={handleOpenMenu}>
        <MoreVertIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorElement} open={menuOpen} onClose={handleCloseMenu}>
        <MenuItem onClick={handleOpenEditDialog}>
          {`Edit ${blockName} Block`}
        </MenuItem>
        <MenuItem onClick={handleOpenAddDialog}>
          + Add New Totals Block
        </MenuItem>
        {creditsHidden && (
          <MenuItem onClick={handleShowCredits}>Show Credits</MenuItem>
        )}
        {!isDefault && (
          <MenuItem onClick={handleOpenConfirmRemove}>
            {`Remove ${blockName} Block`}
          </MenuItem>
        )}
      </Menu>
      {editDialogOpen &&
        (block?.kind === "custom" || block?.kind === "default") && (
          <TotalsBlockDialog
            onClose={() => setEditDialogOpen(false)}
            onSubmit={handleEditBlock}
            coverages={context.payload.coverages}
            existingBlock={block}
          />
        )}
      {addDialogOpen && (
        <TotalsBlockDialog
          onClose={() => setAddDialogOpen(false)}
          onSubmit={handleAddBlock}
          coverages={context.payload.coverages}
        />
      )}
      {!isDefault && (
        <ConfirmationModal
          open={confirmRemoveOpen}
          onCancel={() => setConfirmRemoveOpen(false)}
          onAccept={handleRemoveBlock}
          title="Remove Totals Block"
          description={`Are you sure you want to remove the ${blockName} block? This cannot be undone. Any coverages that are rolling up solely to this block will now roll up to the default Totals block.`}
          confirmButtonText="Remove"
          cancelButtonText="Cancel"
        />
      )}
    </div>
  );
};
